import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Copy, Heart, Loader2, Pencil, RefreshCw, Square, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { api, blobErrMsg, errMsg } from "@/lib/api";

const useSpeaker = (text, voice) => {
  const [state, setState] = useState("idle");
  const audioRef = useRef(null);
  useEffect(() => () => audioRef.current?.pause(), []);
  const toggle = async () => {
    if (state === "playing") { audioRef.current?.pause(); setState("idle"); return; }
    setState("loading");
    try {
      const { data } = await api.post("/speak-reply", { text, voice }, { responseType: "blob", timeout: 30000 });
      const url = URL.createObjectURL(data);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setState("idle"); URL.revokeObjectURL(url); };
      await audio.play();
      setState("playing");
    } catch (e) {
      toast.error(await blobErrMsg(e, "Couldn't play audio."));
      setState("idle");
    }
  };
  return [state, toggle];
};

export const useFavorite = (reply) => {
  const [favId, setFavId] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => setFavId(null), [reply.text]);
  const toggle = async () => {
    setBusy(true);
    try {
      if (favId) {
        await api.delete(`/favorites/${favId}`);
        setFavId(null);
        toast.message("Removed from favourites.");
      } else {
        const { data } = await api.post("/favorites", { text: reply.text, label: reply.label || "", why: reply.why || "" });
        setFavId(data.id);
        toast.success("Saved to favourites.");
      }
    } catch (e) {
      toast.error(errMsg(e, "Couldn't update favourites."));
    } finally {
      setBusy(false);
    }
  };
  return [favId, busy, toggle];
};

const Btn = ({ testId, onClick, children, disabled, active }) => (
  <button data-testid={testId} onClick={onClick} disabled={disabled}
    className={`h-9 px-3 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 border transition-colors disabled:opacity-40 ${active ? "brand-gradient text-white border-transparent" : "border-border hover:border-primary/50"}`}>
    {children}
  </button>
);

export const ReplyCard = ({ reply, index, onRegenerate, onEdit, regenerating, voice, voiceAvailable }) => {
  const n = index + 1;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(reply.text);
  const [why, setWhy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [fallback, setFallback] = useState(false);
  const [tts, toggleTts] = useSpeaker(reply.text, voice);
  const [favId, favBusy, toggleFav] = useFavorite(reply);

  useEffect(() => { setDraft(reply.text); setFallback(false); }, [reply.text]);

  const copy = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("no clipboard");
      await navigator.clipboard.writeText(reply.text);
      setCopied(true);
      toast.success("Copied! Paste it into your chat.");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setFallback(true);
      toast.message("Clipboard blocked — select the text below and copy it manually.");
    }
  };
  const save = () => { if (draft.trim()) onEdit(index, draft.trim()); setEditing(false); };

  return (
    <div data-testid={`reply-card-${n}`} className="panel p-5 rise relative overflow-hidden" style={{ animationDelay: `${index * 0.08}s` }}>
      {regenerating && <div className="absolute inset-0 bg-card/70 backdrop-blur-sm flex items-center justify-center z-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>}
      <div className="flex items-center justify-between mb-3">
        <span data-testid={`reply-label-${n}`} className="text-[11px] uppercase tracking-widest font-bold text-accent">{reply.label}</span>
        <div className="flex items-center gap-2">
          <button data-testid={`favorite-reply-button-${n}`} onClick={toggleFav} disabled={favBusy || editing} aria-pressed={!!favId}
            aria-label={favId ? "Remove from favourites" : "Save to favourites"}
            className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-secondary transition-transform active:scale-90 disabled:opacity-40">
            <Heart className={`h-4 w-4 transition-colors ${favId ? "fill-accent text-accent" : "text-muted-foreground"}`} />
          </button>
          <span className="font-display text-sm font-bold text-muted-foreground/50">0{n}</span>
        </div>
      </div>
      {editing ? (
        <div className="space-y-2">
          <Textarea data-testid={`edit-reply-input-${n}`} value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} maxLength={600} className="rounded-xl" />
          <div className="flex gap-2">
            <Btn testId={`save-reply-button-${n}`} onClick={save} active>Save</Btn>
            <Btn testId={`cancel-edit-button-${n}`} onClick={() => { setDraft(reply.text); setEditing(false); }}>Cancel</Btn>
          </div>
        </div>
      ) : (
        <p data-testid={`reply-text-${n}`} translate="no" className="notranslate text-base sm:text-lg leading-snug font-medium whitespace-pre-wrap">{reply.text}</p>
      )}
      {fallback && (
        <textarea data-testid={`copy-fallback-${n}`} readOnly value={reply.text} onFocus={(e) => e.target.select()} autoFocus
          className="mt-3 w-full rounded-xl border border-primary/40 bg-secondary/40 p-2 text-sm" />
      )}
      {reply.why && (
        <button data-testid={`why-it-works-toggle-${n}`} onClick={() => setWhy(!why)} className="mt-3 text-xs text-muted-foreground inline-flex items-center gap-1 hover:text-foreground">
          Why this fits <ChevronDown className={`h-3 w-3 transition-transform ${why ? "rotate-180" : ""}`} />
        </button>
      )}
      {why && <p data-testid={`reply-why-${n}`} className="mt-1 text-xs text-muted-foreground">{reply.why}</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        <Btn testId={`copy-reply-button-${n}`} onClick={copy} active>{copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}{copied ? "Copied" : "Copy"}</Btn>
        <Btn testId={`regenerate-reply-button-${n}`} onClick={() => onRegenerate(index)} disabled={regenerating}><RefreshCw className="h-3.5 w-3.5" />Regenerate</Btn>
        <Btn testId={`edit-reply-button-${n}`} onClick={() => setEditing(true)} disabled={editing}><Pencil className="h-3.5 w-3.5" />Edit</Btn>
        {voiceAvailable && (
          <Btn testId={`tts-play-button-${n}`} onClick={toggleTts} disabled={tts === "loading"}>
            {tts === "loading" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : tts === "playing" ? <Square className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
            {tts === "playing" ? "Stop" : "Listen"}
          </Btn>
        )}
      </div>
    </div>
  );
};
